<template>
  <div v-if="form.vpnVnCodeInfo" class="stepper-content">
    <!-- L2VNの場合 -->
    <template v-if="form.vpnVnCodeInfo.vnType === 'L2'">
      <!-- VLAN制御 -->
      <!-- 表示：契約変更が可能 & 選択した回線のVLAN制御が「ON」 & サイトをUntagで作成している場合 -->
      <v-row
        v-if="
          form.siteInfo?.lineBandwidth &&
          form.siteInfo?.lineBandwidth.vlanControl === 'ON' &&
          form.siteInfo?.vlanIdType === 'UNTAG'
        "
      >
        <v-col>
          <template v-if="isEdit">
            <app-label-group
              label="VLAN制御"
              :value="$filter.enumTo(form.l2?.isVlanControl, 'on_off')"
            />
          </template>
          <template v-else>
            <app-badge item="VLAN制御" :isRequired="false" :isInfo="false" />
            <v-radio-group inline v-model="form.l2!.isVlanControl" hide-details>
              <v-radio label="ON" :value="true" />
              <v-radio label="OFF" :value="false" />
            </v-radio-group>
          </template>
        </v-col>
      </v-row>
      <template v-if="isShowQos">
        <!-- QoS設定 -->
        <v-row>
          <v-col>
            <app-badge item="QoS設定" :isRequired="false" :isInfo="false" />
            <v-radio-group inline v-model="form.qos!.isQoS" hide-details>
              <v-radio label="ON" :value="true" />
              <v-radio label="OFF" :value="false" />

              <v-btn class="ml-3" color="primary" @click="qosSettingInfo"
                >QoS設定情報</v-btn
              >
            </v-radio-group>
          </v-col>
        </v-row>
        <!-- 割当帯域パターン -->
        <!-- 表示:QoSがON & QoS方式が「帯域制御方式」の場合 -->
        <v-row v-if="form.qos?.isQoS && qos.qosType === 'BANDWIDTH'">
          <v-col>
            <app-badge item="割当帯域パターン" :isInfo="false" />
            <validation-provider
              name="qosBandControlPattern"
              v-model="form.qos.qosBandControlPattern"
              label="割当帯域パターン"
              :rules="{ required: true }"
              v-slot="{ componentField, errorMessage, meta }"
            >
              <app-select
                :items="[
                  { value: 'PATTERN_A', title: 'パターンA' },
                  { value: 'PATTERN_B', title: 'パターンB' },
                  { value: 'PATTERN_C', title: 'パターンC' },
                  { value: 'PATTERN_D', title: 'パターンD' },
                  { value: 'PATTERN_E', title: 'パターンE' },
                ]"
                v-bind="componentField"
                :error-messages="errorMessage"
                :field-meta="meta"
              >
              </app-select>
            </validation-provider>
          </v-col>
        </v-row>
      </template>
    </template>

    <!-- L3VPN/L3VNの場合 -->
    <template v-else>
      <!-- WANアドレス -->
      <v-row>
        <v-col>
          <template v-if="isEdit">
            <app-label-group label="WANアドレス" :value="form.l3?.wanAddress" />
          </template>
          <template v-else>
            <app-badge
              item="WANアドレス"
              :msg="$filter.message(`info_wan_address`)"
            />
            <validation-provider
              name="wanAddress"
              v-model="form.l3!.wanAddress"
              label="WANアドレス"
              :rules="{
                required: true,
                is_ip_subnet: {
                  min: 16,
                  max:
                    form.siteInfo?.accessType === 'IPSEC' ||
                    form.siteInfo?.accessType === 'PLATFORM_GATEWAY'
                      ? 29
                      : 30,
                },
                is_not_loopback_ip: true,
                is_not_multicast_ip: true,
                is_not_class_e_ip: true,
                is_not_broadcast_ip: true,
                is_not_network_ip: true,
              }"
              v-slot="{ componentField, errorMessage, meta }"
            >
              <app-form-input
                v-bind="componentField"
                :field-meta="meta"
                :error-messages="errorMessage"
                :placeholder="
                  $filter.message('placeholder_ip_address_with_subnet30')
                "
              />
            </validation-provider>
          </template>
        </v-col>
      </v-row>
      <!-- ルーティング種別がBGP4の場合 その1 -->
      <template v-if="form.l3!.routingType === 'BGP4'">
        <!-- ピアIPアドレス -->
        <v-row>
          <v-col>
            <app-badge
              item="ピアIPアドレス"
              :msg="$filter.message(`info_ip_address_v4_2`, { host: '2' })"
            />
            <validation-provider
              name="peerIpAddress"
              v-model="form.l3!.bgp4!.peerIpAddress"
              label="ピアIPアドレス"
              :rules="{
                required: true,
                is_ipv4: true,
                cross_wan_address_within_range: {
                  wan: form.l3?.wanAddress,
                },
                is_not_loopback_ip: true,
                is_not_multicast_ip: true,
                is_not_class_e_ip: true,
                is_not_broadcast_ip: {
                  range: form.l3?.wanAddress,
                },
                is_not_network_ip: {
                  range: form.l3?.wanAddress,
                },
              }"
              v-slot="{ componentField, errorMessage, meta }"
            >
              <app-form-input
                v-bind="componentField"
                :field-meta="meta"
                :error-messages="errorMessage"
                :placeholder="
                  $filter.message('placeholder_ip_address_3', { host: '2' })
                "
              />
            </validation-provider>
          </v-col>
        </v-row>
        <!-- AS番号 -->
        <v-row>
          <v-col>
            <template v-if="isFixedAsNumber">
              <app-label-group
                label="AS番号"
                :value="form.l3?.bgp4?.asNumber"
              />
            </template>
            <template v-else>
              <app-badge
                item="AS番号"
                :msg="
                  $filter.message(`info_as_number`, {
                    numberArray:
                      '1～9995、9997～64949、64970～65501、65504～65535',
                  })
                "
              />
              <validation-provider
                name="asNumber"
                v-model.number="form.l3!.bgp4!.asNumber"
                label="AS番号"
                :rules="{
                  required: true,
                  numeric: true,
                  is_number_range: {
                    range: '1-9995,9997-64949,64970-65501,65504-65535',
                  },
                }"
                v-slot="{ componentField, errorMessage, meta }"
              >
                <app-form-input-number
                  v-bind="componentField"
                  :field-meta="meta"
                  :error-messages="errorMessage"
                />
              </validation-provider>
            </template>
          </v-col>
        </v-row>
        <!-- MD5キー -->
        <v-row>
          <v-col>
            <app-badge
              item="MD5キー"
              :isRequired="false"
              :msg="$filter.message(`info_md5`)"
            />
            <validation-provider
              name="md5Key"
              v-model="form.l3!.bgp4!.md5Key"
              label="MD5キー"
              :rules="{
                is_md5: true,
              }"
              v-slot="{ componentField, errorMessage, meta }"
            >
              <app-form-input
                v-bind="componentField"
                :field-meta="meta"
                :error-messages="errorMessage"
              />
            </validation-provider>
          </v-col>
        </v-row>
        <!-- Maximum Prefix -->
        <v-row>
          <v-col>
            <app-badge item="Maximum Prefix" :isInfo="false" />
            <validation-provider
              name="maximumPrefix"
              v-model="form.l3!.bgp4!.maximumPrefix"
              label="Maximum Prefix"
              :rules="{ required: true }"
              v-slot="{ componentField, errorMessage, meta }"
            >
              <app-select
                v-bind="componentField"
                :field-meta="meta"
                :error-messages="errorMessage"
                :items="maximumPrefixOptions"
              />
            </validation-provider>
          </v-col>
        </v-row>
        <!-- Local Preference -->
        <v-row>
          <v-col>
            <app-badge item="Local Preference" :isInfo="false" />
            <app-select
              :items="[
                { value: 'LOW_PRIORITY', title: 'OFF(100)' },
                { value: 'HIGH_PRIORITY', title: '優先(200)' },
              ]"
              v-model="form.l3!.bgp4!.localPreference"
            />
          </v-col>
        </v-row>
        <!-- MED -->
        <v-row>
          <v-col>
            <app-badge item="MED" :isInfo="false" />
            <app-select
              :items="[
                { value: 'HIGH_PRIORITY', title: '非優先(100)' },
                { value: 'LOW_PRIORITY', title: 'OFF(0)' },
              ]"
              v-model="form.l3!.bgp4!.med"
            />
          </v-col>
        </v-row>
      </template>

      <!-- ルーティング種別がStaticの場合 -->
      <template v-else>
        <!-- 経路設定 -->
        <app-label-group label="経路設定" class="mb-3" />
        <v-row class="ml-1">
          <!-- LANアドレスタイトル -->
          <v-col class="address-title">
            <app-badge
              item="LANアドレス"
              :isRequired="form.l3!.static!.length > 0"
              :msg="$filter.message('info_lan_address')"
            />
          </v-col>
          <!-- ネクストホップアドレスタイトル -->
          <v-col class="address-title mr-9">
            <app-badge
              item="ネクストホップアドレス"
              :isRequired="form.l3!.static!.length > 0"
              :msg="$filter.message('info_ip_address_v4_2', { host: '2' })"
            />
          </v-col>
        </v-row>
        <validation-array
          name="static"
          v-slot="{ fields, push, remove }"
          ref="staticAddress"
        >
          <template v-for="(item, index) in fields" :key="item.key">
            <v-row class="ga-2">
              <!-- LANアドレスフォーム -->
              <v-col class="py-0">
                <validation-provider
                  :name="'lan_' + item.key"
                  v-model="form.l3!.static![index].lanAddress"
                  label="LANアドレス"
                  :rules="{
                    // 1以上は必須、特定の回線種別は必須, 片方が入力されている場合は必須
                    required:
                      form.l3!.static?.length !== 0 ||
                      requiredStaticRouting ||
                      !!form.l3!.static![index].nextHopAddress,
                    is_ip_subnet: { min: 0, max: 32, exclude: [31] },
                    is_not_loopback_ip: true,
                    is_not_multicast_ip: true,
                    is_not_class_e_ip: true,
                    is_not_broadcast_ip: true,
                    is_network_address: true,
                    duplicate: { list: lanAddressList(index) },
                  }"
                  v-slot="{ componentField, errorMessage, meta }"
                  :keepValue="true"
                >
                  <app-form-input
                    v-bind="componentField"
                    :field-meta="meta"
                    :error-messages="errorMessage"
                    :placeholder="
                      $filter.message(
                        'placeholder_ip_address_with_subnet24_or_subnet32'
                      )
                    "
                  />
                </validation-provider>
              </v-col>
              <!-- ネクストホップアドレスフォーム -->
              <v-col class="py-0">
                <validation-provider
                  :name="'next_' + item.key"
                  v-model="form.l3!.static![index].nextHopAddress"
                  label="ネクストホップアドレス"
                  :rules="{
                    // 1個以上は必須、特定の回線種別は必須, 片方が入力されている場合は必須
                    required:
                      form.l3!.static?.length !== 0 ||
                      requiredStaticRouting ||
                      !!form.l3!.static![index].lanAddress,
                    is_ipv4: true,
                    cross_wan_address_within_range: {
                      wan: form.l3?.wanAddress,
                    },
                    is_not_loopback_ip: true,
                    is_not_multicast_ip: true,
                    is_not_class_e_ip: true,
                    is_not_broadcast_ip: {
                      range: form.l3?.wanAddress,
                    },
                    is_not_network_ip: {
                      range: form.l3?.wanAddress,
                    },
                  }"
                  v-slot="{ componentField, errorMessage, meta }"
                  :keepValue="true"
                >
                  <app-form-input
                    v-bind="componentField"
                    :field-meta="meta"
                    :error-messages="errorMessage"
                    :placeholder="
                      $filter.message('placeholder_ip_address_3', { host: '2' })
                    "
                  />
                </validation-provider>
              </v-col>

              <v-btn
                icon="$mdiMinus"
                size="small"
                variant="outlined"
                class="rounded-sm"
                @click="removeLAN(index, remove)"
              />
            </v-row>
          </template>
          <v-row justify="end">
            <v-btn
              icon="$mdiPlus"
              size="small"
              variant="outlined"
              class="rounded-sm"
              @click="addLAN(push)"
            />
          </v-row>
        </validation-array>
      </template>

      <!-- QoS -->
      <template v-if="isShowQos">
        <!-- QoS設定 -->
        <v-row>
          <v-col>
            <app-badge item="QoS設定" :isRequired="false" :isInfo="false" />
            <v-radio-group inline v-model="form.qos!.isQoS" hide-details>
              <v-radio label="ON" :value="true" />
              <v-radio label="OFF" :value="false" />

              <v-btn class="ml-3" color="primary" @click="qosSettingInfo"
                >QoS設定情報</v-btn
              >
            </v-radio-group>
          </v-col>
        </v-row>
        <!-- 割当帯域パターン -->
        <!-- 表示:QoSがON & QoS方式が「帯域制御方式」の場合 -->
        <v-row v-if="form.qos!.isQoS && qos.qosType === 'BANDWIDTH'">
          <v-col>
            <app-badge item="割当帯域パターン" :isInfo="false" />
            <validation-provider
              name="qosBandControlPattern"
              v-model="form.qos!.qosBandControlPattern"
              label="割当帯域パターン"
              :rules="{ required: true }"
              v-slot="{ componentField, errorMessage, meta }"
            >
              <app-select
                :items="[
                  { value: 'PATTERN_A', title: 'パターンA' },
                  { value: 'PATTERN_B', title: 'パターンB' },
                  { value: 'PATTERN_C', title: 'パターンC' },
                  { value: 'PATTERN_D', title: 'パターンD' },
                  { value: 'PATTERN_E', title: 'パターンE' },
                ]"
                v-bind="componentField"
                :error-messages="errorMessage"
                :field-meta="meta"
              >
              </app-select>
            </validation-provider>
          </v-col>
        </v-row>
      </template>

      <!-- ルーティング種別がBGP4の場合 その2 -->
      <template v-if="form.l3!.routingType === 'BGP4'">
        <!-- 経路集約 -->
        <v-row>
          <v-col>
            <app-badge item="経路集約" :isRequired="false" :isInfo="false" />
            <v-radio-group
              inline
              v-model="form.l3!.bgp4!.routeAggregation"
              hide-details
            >
              <v-radio label="ON" value="ON" />
              <v-radio label="OFF" value="OFF" />
              <!-- 「CUSTOM」は回線種別「PFアクセスGWⅡ」の場合のみ選択可能 -->
              <v-radio
                label="CUSTOM"
                value="CUSTOM"
                :disabled="form.siteInfo?.accessType !== 'PF_ACCESS_GW2'"
              />
            </v-radio-group>
          </v-col>
        </v-row>
        <!-- 経路集約アドレス -->
        <v-row v-if="form.l3!.bgp4!.routeAggregation !== 'OFF'">
          <v-col>
            <app-badge
              item="経路集約アドレス"
              :isRequired="form.l3!.bgp4!.routeAggregation === 'CUSTOM'"
              :isInfo="form.l3!.bgp4!.routeAggregation === 'CUSTOM'"
              :msg="$filter.message('info_routing_address')"
            >
              <template #others>
                <v-label class="attention ml-2"
                  >※設定反映を行いますと1秒程度の通信断が発生します。</v-label
                >
              </template>
            </app-badge>
            <div class="ml-3" v-if="form.l3!.bgp4!.routeAggregation === 'ON'">
              <span>0.0.0.0/0</span>
            </div>
            <div v-else>
              <validation-array
                name="routeAggregationAddressList"
                v-slot="{ fields, push, remove }"
              >
                <template v-for="(item, index) in fields" :key="item.key">
                  <v-row class="ga-2">
                    <!-- 経路集約アドレスフォーム -->
                    <v-col class="py-0">
                      <validation-provider
                        :name="`route_${item.key}`"
                        v-model="
                          form.l3!.bgp4!.routeAggregationAddressList![index]
                        "
                        label="経路集約アドレス"
                        :rules="{
                          required: true,
                          is_ip_subnet: { min: 1, max: 30 },
                          is_network_address: true,
                          is_not_default_root: true,
                          duplicate: { list: routeAddressList(index) },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                        :keepValue="true"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                          :placeholder="
                            $filter.message(
                              'placeholder_ip_address_with_subnet24'
                            )
                          "
                        />
                      </validation-provider>
                    </v-col>
                    <v-btn
                      icon="$mdiMinus"
                      size="small"
                      variant="outlined"
                      class="rounded-sm"
                      @click="removeRoute(index, remove)"
                    />
                  </v-row>
                </template>
                <v-row justify="end">
                  <v-btn
                    icon="$mdiPlus"
                    size="small"
                    variant="outlined"
                    class="rounded-sm"
                    :disabled="
                      form.l3!.bgp4!.routeAggregationAddressList!.length >= 100
                    "
                    @click="addRoute(push)"
                  />
                </v-row>
              </validation-array>
            </div>
          </v-col>
        </v-row>
      </template>
      <!-- DHCPリレー -->
      <v-row>
        <v-col>
          <app-badge item="DHCPリレー" :isRequired="false" :isInfo="false" />
          <v-radio-group inline v-model="form.l3!.enableDhcpRelay" hide-details>
            <v-radio label="ON" :value="true" />
            <v-radio label="OFF" :value="false" />
          </v-radio-group>
        </v-col>
      </v-row>
      <!-- プライマリサーバ -->
      <app-badge
        item="プライマリサーバ"
        :isRequired="form.l3!.enableDhcpRelay"
        :msg="'・' + $filter.message(`info_ip_address_v4`)"
      />
      <validation-provider
        name="primaryAddress"
        v-model="form.l3!.dhcpRelay!.primaryAddress"
        label="プライマリサーバ"
        :rules="{
          required: form.l3?.enableDhcpRelay,
          is_ipv4: true,
          is_not_current_ip: true,
          is_not_multicast_ip: true,
          is_not_class_e_ip: true,
        }"
        v-slot="{ componentField, errorMessage, meta }"
      >
        <app-form-input
          v-bind="componentField"
          :field-meta="meta"
          :error-messages="errorMessage"
          :placeholder="
            !form.l3?.enableDhcpRelay
              ? null
              : $filter.message('placeholder_ip_address_2')
          "
          :disabled="!form.l3?.enableDhcpRelay"
        />
      </validation-provider>
      <!-- セカンダリサーバ -->
      <app-badge
        item="セカンダリサーバ"
        :isRequired="false"
        :msg="'・' + $filter.message(`info_ip_address_v4`)"
      />
      <validation-provider
        name="secondaryAddress"
        v-model="form.l3!.dhcpRelay!.secondaryAddress"
        label="セカンダリサーバ"
        :rules="{
          is_not_eq: {
            target: '@primaryAddress',
            targetField: 'プライマリサーバ',
          },
          is_ipv4: true,
          is_not_current_ip: true,
          is_not_multicast_ip: true,
          is_not_class_e_ip: true,
        }"
        v-slot="{ componentField, errorMessage, meta }"
      >
        <app-form-input
          v-bind="componentField"
          :field-meta="meta"
          :error-messages="errorMessage"
          :placeholder="
            !form.l3?.enableDhcpRelay
              ? null
              : $filter.message('placeholder_ip_address_2')
          "
          :disabled="!form.l3?.enableDhcpRelay"
        />
      </validation-provider>
    </template>

    <!-- 備考 -->
    <v-row>
      <v-col>
        <app-badge
          item="備考"
          :msg="$filter.message('info_max_length', { maxLength: '20' })"
          :isRequired="false"
        />
        <validation-provider
          name="description"
          v-model="form.description"
          label="備考"
          :rules="{
            max: { length: 20 },
          }"
          v-slot="{ componentField, errorMessage, meta }"
        >
          <app-form-input
            v-bind="componentField"
            :field-meta="meta"
            :error-messages="errorMessage"
          />
        </validation-provider>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { VNConnectForm } from "@/modals/vnConnect/VNConnectEdit.vue";
import QosSettingViewRC from "@/modals/qos/QosSettingViewRC.vue";
import type { QosDetail } from "@/apis/QosApi";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import type { Bgp4Routing } from "@/apis/VNConnectApi";

export default defineComponent({
  name: "VNConnectEditStepper2",
  props: {
    /** true: 編集, false: 追加 */
    isEdit: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    /** Form情報 */
    value: {
      type: Object as PropType<VNConnectForm>,
      required: true,
    },
    /** VPN/VNに紐づくQoS設定詳細 */
    qos: {
      type: Object as PropType<QosDetail>,
      required: true,
    },
    /**
     * 回線の設定上限値.
     * サイト未選択や回線情報のサービス開始日が未来日の場合はundefined
     */
    upperLimitList: {
      type: Array as PropType<UpperLimitEntity[]>,
      required: false,
    },
  },
  expose: ["updateFieldArray"],
  data() {
    return {
      form: this.value,
      maximumPrefix: this.value.l3!.bgp4!.maximumPrefix,
    };
  },
  computed: {
    /** true: 回線種別「IPsec」と「PFGW」が必須, false: 非必須 */
    requiredStaticRouting(): boolean {
      if (this.form.siteInfo) {
        switch (this.form.siteInfo.accessType) {
          case "PLATFORM_GATEWAY":
          case "IPSEC":
            return true;
          default:
            return false;
        }
      } else {
        return false;
      }
    },
    /** MaximumPrefixの選択肢 */
    maximumPrefixOptions(): number[] {
      if (this.upperLimitList) {
        // 設定上限値以下のみにフィルター
        const bgp4Maximum =
          this.upperLimitList!.find(
            (e) => e.upperLimitItemName === "BGP4の最大経路数"
          )?.upperLimit ?? this.maximumPrefix;

        return [
          100, 200, 300, 400, 500, 1000, 1500, 2000, 3000, 4000, 5000,
        ].filter((e) => e <= bgp4Maximum!);
      } else {
        // 設定上限値が取得できない場合（回線のサービス開始日が未来日）は100固定
        return [100];
      }
    },
    /** true: QoS設定を表示（契約変更が可能 & 回線のQoSがON & VPN/VNのQoS設定がON）, false: 非表示 */
    isShowQos(): boolean {
      return (
        this.form.siteInfo!.lineBandwidth?.qos === "ON" &&
        this.qos.qosType !== "NONE"
      );
    },
    /** 経路集約アドレスの重複チェック用リスト作成 */
    routeAddressList() {
      return (index: number) => {
        return this.form.l3!.bgp4!.routeAggregationAddressList
          ? this.form.l3!.bgp4!.routeAggregationAddressList.filter(
              (_, num) => num !== index
            )
          : [];
      };
    },
    /** 経路設定:LANアドレスの重複チェック用リスト作成 */
    lanAddressList() {
      return (index: number) => {
        return this.form.l3!.static
          ? this.form
              .l3!.static.filter((_, num) => num !== index)
              .map((e) => e.lanAddress)
          : [];
      };
    },
    /** true: AS番号が固定値（ラベル）, false: 入力 */
    isFixedAsNumber(): boolean {
      return this.form.siteInfo!.accessType === "PLATFORM_GATEWAY";
    },
  },
  watch: {
    /** 親からのForm情報更新を反映 */
    value(newValue: VNConnectForm) {
      this.form = newValue;
    },
    /** 親へForm情報更新を通知 */
    form: {
      deep: true,
      handler(newValue: VNConnectForm) {
        this.$emit("input", newValue);
      },
    },
    /** 経路集約がカスタムでアドレスリストがnullまたは空の場合は初期化 */
    "form.l3.bgp4.routeAggregation"(newValue: Bgp4Routing["routeAggregation"]) {
      if (
        newValue === "CUSTOM" &&
        (this.form.l3!.bgp4!.routeAggregationAddressList === null ||
          this.form.l3!.bgp4!.routeAggregationAddressList.length === 0)
      ) {
        this.form.l3!.bgp4!.routeAggregationAddressList = [""];
      }
    },
  },
  methods: {
    /** QoS設定情報表示 */
    async qosSettingInfo() {
      await this.$modal.show(QosSettingViewRC, {
        mode: "I",
        isNew: false,
        qosDetail: this.qos,
      });
    },

    /** LANアドレス追加 */
    addLAN(push: (item: any) => void) {
      const addItem = { lanAddress: "", nextHopAddress: "" };
      this.form.l3!.static = [...this.form.l3!.static!, addItem];
      push(addItem);
    },

    /** LANアドレス削除 */
    removeLAN(index: number, remove: (idx: number) => void) {
      if (this.requiredStaticRouting && this.form.l3!.static!.length === 1) {
        this.form.l3!.static = [{ lanAddress: "", nextHopAddress: "" }];
      } else {
        this.form.l3!.static!.splice(index, 1);
        remove(index);
      }
    },

    /** 経路集約アドレス削除 */
    removeRoute(index: number, remove: (idx: number) => void) {
      const list = this.form.l3!.bgp4!.routeAggregationAddressList!;
      if (list.length > 1) {
        this.form.l3!.bgp4!.routeAggregationAddressList!.splice(index, 1);
        remove(index);
      } else {
        this.form.l3!.bgp4!.routeAggregationAddressList = [""];
      }
    },

    /** 経路集約アドレス追加 */
    addRoute(push: (item: any) => void) {
      const addItem = "";
      this.form.l3!.bgp4!.routeAggregationAddressList = [
        ...this.form.l3!.bgp4!.routeAggregationAddressList!,
        addItem,
      ];
      push(addItem);
    },
    updateFieldArray(items: unknown[]) {
      // ステッパー2「NW設定」の経路設定がレンダリングされていない場合、updateFieldArrayメソッドの呼び出しが不要のため、レンダリング可否の判断を行う
      if (this.$refs.staticAddress) {
        (
          this.$refs.staticAddress as {
            replace: (items: unknown[]) => void;
          }
        ).replace([...items]);
      }
    },
  },
});
</script>
<style lang="scss" scoped>
.address-title :deep(.badge-label),
.attention {
  font-size: 0.75rem !important;
}
</style>
